import "./PublishedRuleDetails.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import {getRuleIDStatusRepsone, PublishTestRule,
} from "../TestRule/TestRuleRedux";
import { ToastContainer } from "react-toastify";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";

function PublishedRuleDetails() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const isPublishingRef = useRef(false);
  const ruleId1 = searchParams.get("code_assist_manual_rule_id");
  const environmentParam = searchParams.get("environment");
const publishStorageKey = `published_rule_${ruleId1}_${environmentParam}`;

  const {
    loading: PublishTestRuleLoading,
    data: PublishTestRuleData,
  } = useSelector((state) => state.testRule.PublishTestRuleResponse);

  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishModalType, setPublishModalType] = useState("loading");
  const [ruleStatusResponse, setRuleStatusResponse] = useState(null);


  const handleViewRule = () => {
    navigate("/view-rule-database");
  };

  const wait = (ms) =>
    new Promise((resolve) =>
      setTimeout(resolve, ms)
    );

  const pollRuleStatus = async (jobId) => {
    while (true) {
      try {
        const result = await dispatch(
          getRuleIDStatusRepsone(jobId)
        ).unwrap();


        const items = result?.items || [];

        const isCompleted =
          items.length > 0 &&
          items.every(
            (item) =>
              item?.status === "COMPLETED" ||
              item?.status === "FAILED"
          );

        if (isCompleted) {

          setRuleStatusResponse(result);
          setPublishModalType("result");
          setShowPublishModal(true);
          return;
        }

        await wait(5000);
      } catch (error) {
        console.error(
          "Status API failed:",
          error
        );

        const failedResponse = {
          job_id: jobId,
          items: [
            {
              environment: "-",
              codebook_name: "-",
              status: "FAILED",
              error_message:
                error?.message ||
                "Failed to fetch rule status",
            },
          ],
        };

        setRuleStatusResponse(
          failedResponse
        );

        setPublishModalType("result");

        setShowPublishModal(true);

        return;
      }
    }
  };


  useEffect(() => {
    if (!ruleId1 || !environmentParam) {return}

    const alreadyPublished =
      sessionStorage.getItem(
        publishStorageKey
      );

    if (alreadyPublished) {
      console.log(
        "Publish flow already executed. Skipping Publish API."
      );

      return;
    }

    if (isPublishingRef.current) {
      return;
    }

    isPublishingRef.current = true;

    const publishRule = async () => {
      try {
        let environment;

        try {
          environment = JSON.parse(
            environmentParam
          );
        } catch (error) {
          throw new Error(
            "Invalid environment parameter"
          );
        }


        if (
          !Array.isArray(environment) ||
          environment.length === 0
        ) {
          throw new Error(
            "Environment information is missing"
          );
        }

        sessionStorage.setItem(
          publishStorageKey,
          "true"
        );


        setPublishModalType("loading");

        setRuleStatusResponse(null);

        setShowPublishModal(true);

        const payload = {
          code_assist_manual_rule_id: ruleId1,
        environment,
        };


        const result = await dispatch(
          PublishTestRule(payload)
        ).unwrap();

        const jobId =
          result?.publishing_information?.job_id;

        if (!jobId) {
          throw new Error(
            "Job ID not found in publish response"
          );
        }

        await pollRuleStatus(jobId);
      } catch (error) {
        console.error(
          "Publish API error:",
          error
        );

        const failedResponse = {
          job_id: "-",
          items: [
            {
              environment: "-",
              codebook_name: "-",
              status: "FAILED",
              error_message:
                error?.message ||
                "Publishing failed",
            },
          ],
        };

        setRuleStatusResponse(failedResponse);
        setPublishModalType("result");
        setShowPublishModal(true);
      } finally {
        isPublishingRef.current = false;
      }
    };

    publishRule();

  }, [
    dispatch,
    ruleId1,
    environmentParam,
  ]);


  const handleClosePublishModal = () => {
    setShowPublishModal(false);
  };

  let environmentDisplay = "-";

  try {
    const parsedEnvironment = JSON.parse(
      environmentParam || "[]"
    );

    if (Array.isArray(parsedEnvironment)) {
      environmentDisplay =
        parsedEnvironment.join(", ");
    }
  } catch (error) {
    environmentDisplay = "-";
  }


  const resultItems =
    ruleStatusResponse?.items || [];

  const isPublishSuccess =
    resultItems.length > 0 &&
    resultItems.every(
      (item) =>
        item?.status === "COMPLETED"
    );

  const failedItems =
    resultItems.filter(
      (item) =>
        item?.status === "FAILED"
    );

  return (
    <>
    <div className="publishedRule-page">
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Header */}
      <div className="publishedRule-header">
        <div className="publishedRule-header-title-row">
          <h1 className="publishedRule-title">
              {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData?.rule_information?.description ||"Description..."}
          </h1>
          <span className="publishedRule-id-pill">
              {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData?.rule_id ||
                    "Rule ID..."}
          </span>
          <span className="publishedRule-status-badge publishedRule-status-badge--active">
            <span className="publishedRule-status-dot" aria-hidden="true" />
            ACTIVE
          </span>
        </div>
      </div>

      {/* Rule Information */}
      <section className="publishedRule-card">
        <div className="publishedRule-card-header">
          <span className="publishedRule-card-icon" aria-hidden="true">
            &#9432;
          </span>
          Rule Information
        </div>
        <div className="publishedRule-card-body">
          <div className="publishedRule-grid">
            <div className="publishedRule-field">
              <div className="publishedRule-label">Rule ID</div>
              <div className="publishedRule-value">
                  {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData?.rule_id ||
                      "-"}
                </div>
              </div>
          <div className="publishedRule-field">
            <div className="publishedRule-label">
              Author
            </div>

              <div className="publishedRule-value">
                  {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData
                        ?.rule_information
                        ?.author || "-"}
                </div>
              </div>
         <div className="publishedRule-field">
            <div className="publishedRule-label">
               Creation Date
            </div>

              <div className="publishedRule-value">
                {PublishTestRuleLoading ? "Loading..." : PublishTestRuleData ?.rule_information ?.created_on || "-"}
                </div>
              </div>


              <div className="publishedRule-field">
            <div className="publishedRule-label">
              Clinical Domain
            </div>
              <div className="publishedRule-value">
                <span className="publishedRule-domainTag">
                  {PublishTestRuleLoading
                    ? "Loading..."
                    : PublishTestRuleData
                          ?.rule_information
                          ?.clinical_domain || "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Rule Configuration */}
      <section className="publishedRule-card">
        <div className="publishedRule-card-header">
          <span
              className="publishedRule-card-icon"
              aria-hidden="true"
            >
            &#9635;
          </span>

          Rule Configuration
        </div>

        <div className="publishedRule-card-body">

          <div className="publishedRule-field">
            <div className="publishedRule-label">Trigger Context</div>
            <div className="publishedRule-value">
                    {
  PublishTestRuleLoading
    ? "Loading..."
    : PublishTestRuleData?.rule_configuration?.pathway_entry
      ? `${PublishTestRuleData.rule_configuration.pathway_entry.type}${
          PublishTestRuleData.rule_configuration.pathway_entry.value
            ? ` - ${PublishTestRuleData.rule_configuration.pathway_entry.value}`
            : ""
        }`
      : "-"
}
              </div>
            </div>
            <div className="publishedRule-field publishedRule-field--spaced">
              <div className="publishedRule-label">
                Target ICD-10 Codes
              </div>

            <div className="publishedRule-tags">
              {PublishTestRuleLoading ? (
                "Loading..."
              ) : (
                  <>
                    {(
                      PublishTestRuleData
                        ?.rule_configuration
                        ?.trigger_codes || []
                    ).length > 0 ? (
                PublishTestRuleData.rule_configuration.trigger_codes.map(
                  (item, index) => (
                    <span
                      className="publishedRule-tag"
                      key={`${item?.code || "code"}-${index}`}
                    >
                      <span className="publishedRule-tag-code">
                        {item?.code || ""}
                      </span>

                      <span className="publishedRule-tag-desc">
                        {item?.description || ""}
                      </span>
                    </span>
                  ),
                )
              ) : (
                <span className="publishedRule-tag">No code</span>
              )}
            </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Publishing Information */}
      <section className="publishedRule-card publishedRule-card--publishing">
        <div className="publishedRule-card-header">
          <span className="publishedRule-card-icon" aria-hidden="true">
            &#9998;
          </span>
          Publishing Information
        </div>
        <div className="publishedRule-card-body publishedRule-card-body--rows">
          <div className="publishedRule-row">
            <div className="publishedRule-rowLabel">Publish Status</div>
            <div className="publishedRule-rowValue publishedRule-rowValue--link">
              {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData
                    ?.publishing_information
                    ?.status || "-"}
            </div>
          </div>
          <div className="publishedRule-row">
            <div className="publishedRule-rowLabel">Publishing Environment</div>
            <div className="publishedRule-rowValue">
              {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData
                    ?.publishing_information
                    ?.environment?.[0] || "-"}
                    </div>
            </div>
            <div className="publishedRule-row">
              <div className="publishedRule-rowLabel">
                Published By
          </div>
          <div className="publishedRule-rowValue">
                {PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData
                      ?.publishing_information
                      ?.published_by || "-"}
</div>
          </div>
          <div className="publishedRule-row publishedRule-row--last">
            <div className="publishedRule-rowLabel">Published On</div>
            <div className="publishedRule-rowValue">{PublishTestRuleLoading
                  ? "Loading..."
                  : PublishTestRuleData
                    ?.publishing_information
                    ?.published_on || "-"}</div>
          </div>
        </div>
      </section>
      <div className="publishedRule-footer">
        <button
          type="button"
          className="publishedRule-btn publishedRule-btn--primary"
          disabled={PublishTestRuleLoading}
          onClick={handleViewRule}
        >
          View Rule
        </button>
      </div>
    </div>
      {showPublishModal && (
        <div className="publishedRule-modal-overlay">
          <div className="publishedRule-info-modal">

            <div className="publishedRule-info-modal-header">
              <h3 className="publishedRule-info-modal-header-text">
                {publishModalType === "loading"
                  ? "Publishing Rule"
                  : "Publishing Information"}
              </h3>

              {publishModalType === "result" && (
                <button
                  type="button"
                  onClick={handleClosePublishModal}
                  className="publishedRule-modal-close"
                >
                  <CloseIcon />
                </button>
              )}
            </div>

            <div className="publishedRule-info-modal-body">
              {publishModalType === "loading" && (
                <div className="publishedRule-loading-content">
                  <div className="publishedRule-spinner" />
                  <div className="publishRule-loader">
                    <div className="publishRule-loader">
                      <svg viewBox="0 0 48 48">
                        {/* Fixed gray square */}
                        <rect
                          x="3"
                          y="3"
                          width="42"
                          height="42"
                          rx="8"
                          fill="none"
                          stroke="#e0e0e0"
                          strokeWidth="3"
                        />

                        {/* Animated blue line */}
                        <rect
                          className="publishRule-loader-progress"
                          x="3"
                          y="3"
                          width="42"
                          height="42"
                          rx="8"
                          fill="none"
                          stroke="#1976d2"
                          strokeWidth="3"
                          strokeLinecap="round"
                        />

                        {/* Center dot */}
                        <circle cx="24" cy="24" r="3" fill="#1976d2" />
                      </svg>
                    </div>
                  </div>
                  <h5>Please wait...</h5>
                  <p>
                    Your rule is being published to
                    <strong className="publishedRule-result-content-env">
                      {" "}
                      {environmentDisplay}
                    </strong>
                    . This may take a few moments.
                  </p>
                  <p className="publishedRule-result-content-loader-info">
                    <InfoIcon color="inherit" sx={{ mr: 1 }} />
                    Please do not close or refresh this page until publishing is
                    complete.
                  </p>
                </div>
              )}

              {publishModalType === "result" && (
                <div className="publishedRule-result-content">
                  {isPublishSuccess ? (
                    <>
                      <CheckCircleIcon
                        fontSize="large"
                        sx={{ color: "#059669" }}
                      />
                      <h5>Rule Published Successfully</h5>
                      <p>
                        Your rule has been successfully published to{" "}
                        <strong className="publishedRule-result-content-env">
                          {environmentDisplay}
                        </strong>
                      </p>
                    </>
                  ) : (
                    <>
                      <ErrorIcon fontSize="large" sx={{ color: "red" }} />
                      <h5>Rule Publishing Failed</h5>
                      <p>We couldn't complete the publishing of your rule.</p>

                      {failedItems.length > 0 && (
                        <div className="publishedRule-error-message">
                          {failedItems.length > 0 && (
                            <div className="publishedRule-error-message1">
                              Some items could not be published. Please try again.
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {publishModalType === "result" && (
              <div className="publishedRule-info-modal-footer">
                <button
                  type="button"
                  className="publishedRule-btn publishedRule-btn--primary"
                  onClick={
                    handleClosePublishModal
                  }
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default PublishedRuleDetails;